import {
	IconButton,
	ReorderableList,
	Typography,
	useReorderableListItemDraggableModeForEditMode
} from "client/Components";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { MailboxesManagementActionMenu } from "./MailboxesManagementActionMenu";
import {
	AvailableMailboxSaveModel,
	createMailboxViewModelForNewMailbox,
	MailboxType,
	MainMailboxSaveModel,
	SharedMailboxSaveModel,
} from "client/Api/Email/Mailboxes/mailboxes";
import { getClientId,
	hasClientId,
	tryGetClientId
} from "client/Misc/clientId";
import { EditableListItemContent } from "./EditableListItemContent";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { never } from "client_legacy/ndaw/errors";
import { contains } from "client/Data/searching";
import { Guid } from "client_legacy/ndaw/guid";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { useItemMenuState } from "client/Hooks";

export type MailboxesOrderableListProps = {
	mailboxesHandler: IValueInputAttachable<AvailableMailboxSaveModel[]>;
	mainEmail: string;
	searchTerm: string;
	onItemAdded: () => void;
};

export const MailboxesOrderableList = (
	props: MailboxesOrderableListProps
): ReactElement => {
	const {
		mailboxesHandler,
		searchTerm,
		mainEmail,
		onItemAdded
	} = props;

	const [itemToFocusId, setItemToFocusId] = useState<number | Guid | null>(null);

	const nextOrderNumber: number = useMemo(
		() =>
			Math.max(
				...mailboxesHandler
					.map(
						(mailboxAttachable) => mailboxAttachable.value.sortOrder
					)
			) + 1,
		[mailboxesHandler]
	);

	const doesMatchSearchTerm = useCallback(
		(availableMailbox: AvailableMailboxSaveModel): boolean => {
			if (searchTerm === "") {
				return true;
			}
			switch (availableMailbox.type) {
				case MailboxType.Main:
					return contains(mainEmail, searchTerm);
				case MailboxType.Manual:
					return contains(
						availableMailbox.emailAddress,
						searchTerm
					);
				default:
					never(availableMailbox);
			}
		},
		[mainEmail, searchTerm]
	);

	const addItemHandler = useCallback(
		(): void => {
			const newMailbox: SharedMailboxSaveModel = createMailboxViewModelForNewMailbox(
				"",
				nextOrderNumber,
				MailboxType.Manual
			);

			mailboxesHandler.reducer
				.push(
					newMailbox
				);

			const newMailboxId: number | Guid = hasClientId(newMailbox)
				? getClientId(newMailbox)
				: newMailbox.id;

			onItemAdded();
			setItemToFocusId(newMailboxId);
		},
		[
			mailboxesHandler,
			nextOrderNumber,
			onItemAdded
		]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			let mailboxesCopy: AvailableMailboxSaveModel[] = [
				...mailboxesHandler.value,
			];
			mailboxesCopy.splice(
				toIndex,
				0,
				mailboxesCopy.splice(fromIndex, 1)[0]
			);

			mailboxesCopy = mailboxesCopy.map(
				(mailbox: AvailableMailboxSaveModel, index: number) => ({
					...mailbox,
					sortOrder: index,
				})
			);

			mailboxesHandler.reducer
				.setValue(mailboxesCopy);
		},
		[mailboxesHandler.reducer, mailboxesHandler.value]
	);

	const renderRowItems = useCallback(
		(
			mailboxAttachable: IObjectInputAttachable<AvailableMailboxSaveModel>,
			inputIndex: number
		) => {
			return mailboxAttachable.whenType(
				x => x.type === MailboxType.Manual,
				(sharedMailboxAttachable) => {
					const id = hasClientId(mailboxAttachable.value)
						? getClientId(mailboxAttachable.value)
						: mailboxAttachable.value.id;

					return (
						<SharedMailboxItem
							key={id}
							mailboxAttachable={sharedMailboxAttachable}
							index={inputIndex}
							onBlur={() => setItemToFocusId(null)}
							initialHasFocus={id === itemToFocusId}
							mailboxesHandler={mailboxesHandler}
						/>
					);
				},
				(mainMailboxAttachable) => (
					<MainMailboxItem
						key={mainMailboxAttachable.value.id}
						mailboxAttachable={mainMailboxAttachable}
						mailboxesHandler={mailboxesHandler}
						mainEmail={mainEmail}
					/>
				)
			);
		},
		[
			itemToFocusId,
			mailboxesHandler,
			mainEmail
		]
	);

	const getOverlayTextHandler = useCallback(
		(index: number): string => {
			const value: AvailableMailboxSaveModel = mailboxesHandler.forIndex(index)
				.value;

			return isNotNil(value)
				? value.type === MailboxType.Manual
					? value.emailAddress
					: mainEmail
				: "";
		},
		[mailboxesHandler, mainEmail]
	);

	return (
		<>
			<ReorderableList
				onItemMove={!searchTerm ? itemMoveHandler : undefined}
				trackingId="mail-boxes-reorderable-list"
				hasSeparator
				getOverlayText={getOverlayTextHandler}
			>
				{mailboxesHandler
					.map((mailboxAttachable, inputIndex) =>
						mailboxAttachable.when(
							doesMatchSearchTerm,
							(filteredMailboxAttachable) =>
								renderRowItems(
									filteredMailboxAttachable,
									inputIndex
								)
						)
					)
				}
			</ReorderableList>
			<ReorderableList.AddButton
				trackingId="add-mailbox-button"
				onClick={addItemHandler}
			/>
		</>
	);
};

MailboxesOrderableList.displayName = "MailboxesOrderableList";

type SharedMailboxItemProps = {
	mailboxesHandler: IValueInputAttachable<AvailableMailboxSaveModel[]>;
	mailboxAttachable: IObjectInputAttachable<SharedMailboxSaveModel>;
	index: number;
	onBlur: () => void;
	initialHasFocus: boolean;
};

const SharedMailboxItem = (props: SharedMailboxItemProps) => {
	const {
		mailboxesHandler,
		mailboxAttachable,
		index,
		onBlur,
		initialHasFocus
	} = props;

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const moreButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const {
		rowDraggableMode,
		onToggleEditMode
	} = useReorderableListItemDraggableModeForEditMode();

	const visibilityHandler: () => void = useCallback(
		(): void => {
			mailboxAttachable.reducer
				.setValue(
					{
						...mailboxAttachable.value,
						isVisible: !mailboxAttachable.value
							.isVisible,
					}
				);
		},
		[mailboxAttachable.reducer, mailboxAttachable.value]
	);

	const isEditable: boolean = useMemo(
		() => mailboxAttachable.value
			.type === MailboxType.Manual,
		[mailboxAttachable.value.type]
	);

	const isVisible: boolean = useMemo(
		() => mailboxAttachable.value
			.isVisible,
		[mailboxAttachable.value.isVisible]
	);

	const idForTrackingId: number | Guid| undefined = mailboxAttachable.value.id !== 0
		? mailboxAttachable.value.id
		: tryGetClientId(mailboxAttachable.value);

	return (
		<>
			<ReorderableList.RowItem
				trackingId={generateTrackingId("reorderable-list-row-item",	`|shared-${idForTrackingId}`)}
				validationMessage={
					mailboxAttachable.for("emailAddress")
						.attachToValidationError()
						.message
				}
				dontEllipseText={isEditable}
				actions={
					<>
						<IconButton
							icon={{
								kind: "font",
								name: isVisible ? "eye" : "eye-slash",
								color: isVisible ? "success" : "danger"
							}}
							kind="transparent-colored"
							onClick={visibilityHandler}
							trackingId="visibility-button"
						/>
						<IconButton
							ref={moreButtonRef}
							onClick={() => onOpenMoreMenu()}
							icon="ellipsis"
							kind="transparent"
							trackingId="more"
							shape="round"
							isActive={menuState.isOpen && !menuState.isContextMenu}
						/>
					</>
				}
				isActive={menuState.isOpen}
				mouseInteraction={{
					onContextMenu: onOpenContextMenu,
					draggableMode: rowDraggableMode
				}}
			>
				{isEditable
					? (
						<EditableListItemContent
							inputAttachable={mailboxAttachable.for("emailAddress")}
							onBlur={() => onBlur()}
							initialHasFocus={initialHasFocus}
							onToggleEditMode={onToggleEditMode}
						/>
					)
					: (
						<Typography weight="bold">
							{
								mailboxAttachable.for("emailAddress")
									.value
							}
						</Typography>
					)
				}
			</ReorderableList.RowItem>
			{menuState.isOpen && (
				<MailboxesManagementActionMenu
					target={ensureNotNil(moreButtonRef.current)}
					onClose={onCloseMenu}
					inputAttachable={mailboxAttachable}
					mailboxesHandler={mailboxesHandler}
					inputIndex={index}
					name={
						mailboxAttachable.value
							.emailAddress
					}
					menuState={menuState}
				/>
			)}
		</>
	);
};

SharedMailboxItem.displayName = "SharedMailboxItem";

type MainMailboxItemProps = {
	mailboxesHandler: IValueInputAttachable<AvailableMailboxSaveModel[]>;
	mailboxAttachable: IObjectInputAttachable<MainMailboxSaveModel>;
	mainEmail: string;
};

const MainMailboxItem = (props: MainMailboxItemProps) => {
	const {
		mailboxAttachable,
		mailboxesHandler,
		mainEmail
	} = props;

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const moreButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	return (
		<>
			<ReorderableList.RowItem
				trackingId={generateTrackingId("reorderable-list-row-item", "|main")}
				actions={
					<>
						<IconButton
							icon="eye"
							kind="transparent"
							trackingId="visibility-button"
							isPlaceholder
						/>
						<IconButton
							ref={moreButtonRef}
							onClick={() => onOpenMoreMenu()}
							icon="ellipsis"
							kind="transparent"
							trackingId="more"
							shape="round"
							isActive={menuState.isOpen && !menuState.isContextMenu}
						/>
					</>
				}
				isActive={menuState.isOpen}
				mouseInteraction={{
					onContextMenu: onOpenContextMenu
				}}
			>
				<Typography weight="bold">{mainEmail}</Typography>
			</ReorderableList.RowItem>
			{menuState.isOpen && (
				<MailboxesManagementActionMenu
					target={ensureNotNil(moreButtonRef.current)}
					onClose={onCloseMenu}
					inputAttachable={mailboxAttachable}
					mailboxesHandler={mailboxesHandler}
					name={mainEmail}
					menuState={menuState}
				/>
			)}
		</>
	);
};

MainMailboxItem.displayName = "MainMailboxItem";
